import React, { useCallback } from 'react'
import { formatDurationSeconds, secondsUntil } from '../../utils/time'
import {
  CountdownState,
  Dispatch,
  RecordingPausedState,
  RecordingState,
  recordingCountdownSeconds,
} from '../webRecorder2'
import { recordingDurationSeconds } from '../recorder'
import { TrashIcon } from '@heroicons/react/24/outline'
import { IconButton } from '~/components/IconButton'
import { Modal } from '~/components/Modal'
import { take } from '~/utils/map'
import { StartRecordingIcon } from './RecordingIcons'
import { PauseButton } from './PauseButton'
import { DownloadButton } from './DownloadButton'
import { TickUpdated } from './TickUpdated'

export function InitialView({ dispatch }: { dispatch: Dispatch }) {
  return (
    <IconButton
      icon={<StartRecordingIcon size="sm" />}
      screenReaderText="Capture meeting now"
      tooltip="Capture meeting now"
      variant="outline"
      onClick={() => dispatch({ action: 'start-new-recording' })}
    />
  )
}

export function RecordingView({ state, dispatch }: { state: RecordingState; dispatch: Dispatch }) {
  const currentDuration = useCallback(
    (now: number) => {
      const times = take(state.recorder, ['prevDurationMs', 'startTimeMs'])
      const durationSeconds = recordingDurationSeconds(times, now)
      return <span className="tabular-nums inline-block">{formatDurationSeconds(durationSeconds)}</span>
    },
    [state.recorder.prevDurationMs, state.recorder.startTimeMs]
  )

  return (
    <div className="flex flex-row gap-2 items-center">
      <PauseButton onClick={() => dispatch({ action: 'pause-recording' })} size="sm" />
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          Capturing
          <TickUpdated currentValue={currentDuration} />
        </div>
      </div>
    </div>
  )
}

export function RecordingPausedView({
  state,
  canDownload,
  dispatch,
}: {
  state: RecordingPausedState
  canDownload: boolean
  dispatch: Dispatch
}) {
  return (
    <div className="flex flex-row gap-2 items-center">
      <Modal
        title="Delete captured meeting?"
        trigger={
          <IconButton
            icon={<TrashIcon />}
            screenReaderText="Delete captured meeting"
            tooltip="Delete captured meeting"
          />
        }
        confirm={
          <button
            className="pc-button pc-button--danger pc-button--sm bg-danger-inverse py-2"
            onClick={() => dispatch({ action: 'delete-recording' })}
          >
            Delete
          </button>
        }
      >
        <p className="max-w-[26rem]">Are you sure you want to delete this capture? This action cannot be undone.</p>
      </Modal>
      {canDownload && <DownloadButton onClick={() => dispatch({ action: 'download-recording' })} />}
      <ResumeButton onClick={() => dispatch({ action: 'resume-recording' })} disabled={state.resuming} />
      Paused {formatDurationSeconds(recordingDurationSeconds(state.recorder))}
    </div>
  )
}

function ResumeButton({ onClick, disabled }: { onClick: () => void; disabled: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`
        w-5 h-5 relative group
        hover:scale-[130%] transition-transform duration-100
      `}
    >
      <span className="sr-only">Resume capture</span>
      <div className="w-[18px] h-[18px] left-px top-px absolute bg-primary rounded-full border border-secondary" />
      <div
        className={`
          w-3 h-3 left-1 top-1 absolute bg-danger-icon rounded-2xl
          group-active:w-[16px] group-active:h-[16px] group-active:left-[2px] group-active:top-[2px] transition-all duration-100
        `}
      />
    </button>
  )
}

export function RecordingCountdownView({ state: { recordingStartTimeMs, stream } }: { state: CountdownState }) {
  return (
    <div className="flex flex-row gap-2 items-center text-primary text-sm">
      Capture starting in{' '}
      <span className="min-w-4 inline-block">
        {recordingStartTimeMs && stream ? (
          <TickUpdated currentValue={(now) => Math.max(0, secondsUntil(recordingStartTimeMs, now))} />
        ) : (
          recordingCountdownSeconds
        )}
      </span>
    </div>
  )
}
